import { API, GraphQLQuery, GraphQLResult } from 'src/utils/apolloApi';
import { listUserEventsByUserId } from 'src/graphql/queries';
import * as mutations from 'src/graphql/mutations';
import {
  EventType,
  ListUserEventsByUserIdQuery,
  ListUserEventsByUserIdQueryVariables,
  CreateUserEventMutation,
  CreateUserEventMutationVariables,
  CreateUserEventInput,
  DeviceType,
  ModelSortDirection,
} from 'src/API';
import { detectDeviceType } from 'src/utils/detectDeviceType';

/**
 * ユーザーイベントを作成する
 * @param userEventInput ユーザーイベントの入力データ
 * @returns 作成されたユーザーイベント
 */
export const createUserEvent = async (userEventInput: {
  userId: string;
  eventType: EventType;
  timestamp: string;
  device: DeviceType;
}) => {
  try {
    const input: CreateUserEventInput = {
      userId: userEventInput.userId,
      eventType: userEventInput.eventType,
      timestamp: userEventInput.timestamp,
      device: userEventInput.device,
    };
    const variables: CreateUserEventMutationVariables = { input };
    const result = await API.graphql<GraphQLQuery<CreateUserEventMutation>>({
      query: mutations.createUserEvent,
      variables,
    });
    return result.data?.createUserEvent;
  } catch (error) {
    console.error('Error creating UserEvent:', error);
    throw error;
  }
};

/**
 * ログインイベントを作成する
 * @param userId ユーザーID
 * @returns 作成されたユーザーイベント
 */
export const createLoginEvent = async (userId: string) => {
  try {
    const userEventInput = {
      userId,
      eventType: EventType.LOGIN,
      timestamp: new Date().toISOString(),
      device: detectDeviceType(),
    };

    await createUserEvent(userEventInput);
  } catch (error) {
    console.error('Error creating UserEvent:', error);
    throw error;
  }
};

/**
 * サインインイベントを作成する
 * @param userId ユーザーID
 */
export const createSigninEvent = async (userId: string) => {
  try {
    const userEventInput = {
      userId,
      eventType: EventType.SIGNIN,
      timestamp: new Date().toISOString(),
      device: detectDeviceType(),
    };

    await createUserEvent(userEventInput);
  } catch (error) {
    console.error('Error creating UserEvent:', error);
    throw error;
  }
};

/**
 * ユーザー削除イベントを作成する
 * @param userId ユーザーID
 * @returns 作成されたユーザーイベント
 */
export const createUserDeletedEvent = async (userId: string) => {
  try {
    const userEventInput = {
      userId,
      eventType: EventType.USER_DELETED,
      timestamp: new Date().toISOString(),
      device: detectDeviceType(),
    };

    await createUserEvent(userEventInput);
  } catch (error) {
    console.error('Error creating UserEvent:', error);
    throw error;
  }
};

/**
 * 指定ユーザーの最終ログイン日時を取得する
 * @param userId ユーザーID
 * @returns 最終ログイン日時（ISO文字列）またはnull
 */
export const fetchLastLoginDateByUserId = async (userId: string): Promise<string | null> => {
  try {
    const variables: ListUserEventsByUserIdQueryVariables = {
      userId,
      sortDirection: ModelSortDirection.DESC,
      filter: { eventType: { eq: EventType.LOGIN } },
      limit: 5,
    };
    const result = await API.graphql<GraphQLQuery<ListUserEventsByUserIdQuery>>({
      query: listUserEventsByUserId,
      variables,
    });
    const items =
      result.data?.listUserEventsByUserId?.items?.filter(
        (item): item is NonNullable<typeof item> => item !== null
      ) || [];
    return items.length > 0 ? items[0].timestamp || null : null;
  } catch (e) {
    console.error('Error fetching last login date:', e);
    return null;
  }
};

/**
 * 指定ユーザーが指定期間内にログインしたかどうかを取得する
 * @param userId ユーザーID
 * @param startDate 期間開始日（ISO文字列）
 * @param endDate 期間終了日（ISO文字列）
 * @returns 期間内にログインがあればtrue、なければfalse
 */
export const hasUserLoggedInDuringPeriod = async (
  userId: string,
  startDate: string,
  endDate: string
): Promise<boolean> => {
  try {
    const variables: ListUserEventsByUserIdQueryVariables = {
      userId,
      timestamp: {
        between: [startDate, endDate],
      },
      filter: {
        eventType: { eq: EventType.LOGIN },
      },
      limit: 5,
    };
    const result = await API.graphql<GraphQLQuery<ListUserEventsByUserIdQuery>>({
      query: listUserEventsByUserId,
      variables,
    });
    const items =
      result.data?.listUserEventsByUserId?.items?.filter(
        (item): item is NonNullable<typeof item> => item !== null
      ) || [];
    return items.length > 0;
  } catch (e) {
    console.error('Error checking user login during period:', e);
    return false;
  }
};

export const getFirstSigninTimestamp = async (userId: string): Promise<string | null> => {
  try {
    const variables: ListUserEventsByUserIdQueryVariables = {
      userId,
      sortDirection: ModelSortDirection.ASC,
      filter: { eventType: { eq: EventType.SIGNIN } },
      limit: 5,
    };
    const result = await API.graphql<GraphQLQuery<ListUserEventsByUserIdQuery>>({
      query: listUserEventsByUserId,
      variables,
    });
    const items =
      result.data?.listUserEventsByUserId?.items?.filter(
        (item): item is NonNullable<typeof item> => item !== null
      ) || [];
    const value = items.length > 0 ? items[0].timestamp || null : null;
    return value;
  } catch (e) {
    console.error('Error fetching first signin timestamp:', e);
    return null;
  }
};

export const getLastUserDeletedTimestamp = async (userId: string): Promise<string | null> => {
  try {
    const variables: ListUserEventsByUserIdQueryVariables = {
      userId,
      sortDirection: ModelSortDirection.DESC,
      filter: { eventType: { eq: EventType.USER_DELETED } },
      limit: 5,
    };
    const result = await API.graphql<GraphQLQuery<ListUserEventsByUserIdQuery>>({
      query: listUserEventsByUserId,
      variables,
    });
    const items =
      result.data?.listUserEventsByUserId?.items?.filter(
        (item): item is NonNullable<typeof item> => item !== null
      ) || [];
    const value = items.length > 0 ? items[0].timestamp || null : null;
    return value;
  } catch (e) {
    console.error('Error fetching first user deleted timestamp:', e);
    return null;
  }
};

/**
 * 指定ユーザーの最初のログイン日時を取得する
 * @param userId ユーザーID
 * @returns 最初のログイン日時（ISO文字列）またはnull
 */
export const getFirstLoginTimestamp = async (userId: string): Promise<string | null> => {
  try {
    const variables: ListUserEventsByUserIdQueryVariables = {
      userId,
      sortDirection: ModelSortDirection.ASC,
      filter: { eventType: { eq: EventType.LOGIN } },
      limit: 5,
    };
    const result = await API.graphql<GraphQLQuery<ListUserEventsByUserIdQuery>>({
      query: listUserEventsByUserId,
      variables,
    });
    const items =
      result.data?.listUserEventsByUserId?.items?.filter(
        (item): item is NonNullable<typeof item> => item !== null
      ) || [];
    const value = items.length > 0 ? items[0].timestamp || null : null;
    return value;
  } catch (e) {
    console.error('Error fetching first login timestamp:', e);
    return null;
  }
};

/**
 * 指定期間内のユーザー別デバイスログイン数を取得する
 * @param userId ユーザーID
 * @param startDate 期間開始日（ISO文字列）
 * @param endDate 期間終了日（ISO文字列）
 * @returns デバイス別ログイン数 { desktop: number, mobile: number }
 */
export const getUserDeviceLoginCounts = async (
  userId: string,
  startDate: string,
  endDate: string
): Promise<{ desktop: number; mobile: number }> => {
  try {
    let allItems: NonNullable<ListUserEventsByUserIdQuery['listUserEventsByUserId']>['items'] = [];
    let nextToken: string | null | undefined = undefined;

    do {
      const result: GraphQLResult<ListUserEventsByUserIdQuery> = await API.graphql<
        GraphQLQuery<ListUserEventsByUserIdQuery>
      >({
        query: listUserEventsByUserId,
        variables: {
          userId,
          timestamp: {
            between: [startDate, endDate],
          },
          filter: {
            eventType: { eq: EventType.LOGIN },
          },
          limit: 1000,
          nextToken,
        },
      });

      const items = result.data?.listUserEventsByUserId?.items || [];
      allItems = [...allItems, ...items];
      nextToken = result.data?.listUserEventsByUserId?.nextToken;
    } while (nextToken);

    const filteredItems = allItems.filter(
      (item): item is NonNullable<typeof item> => item !== null
    );

    const desktopCount = filteredItems.filter((item) => item.device === DeviceType.DESKTOP).length;
    const mobileCount = filteredItems.filter((item) => item.device === DeviceType.MOBILE).length;

    return { desktop: desktopCount, mobile: mobileCount };
  } catch (e) {
    console.error('Error fetching user device login counts:', e);
    return { desktop: 0, mobile: 0 };
  }
};
